import React, { useState, useEffect } from 'react';
import { servicesData } from '../data/servicesData';
import ServiceModal from '../components/ServiceModal';
import ServiceProcess from '../components/ServiceProcess';
import '../styles/components/ServicesPage.scss';

const ServicesPage = () => {
  const [selectedService, setSelectedService] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [animatedServices, setAnimatedServices] = useState([]);

  const serviceStats = [
    { number: '50+', label: 'Projects Completed', icon: 'fas fa-project-diagram' },
    { number: '40,000+', label: 'Respondents Reached', icon: 'fas fa-users' },
    { number: '40+', label: 'Districts Covered', icon: 'fas fa-map-marker-alt' },
    { number: '25+', label: 'Major Clients', icon: 'fas fa-handshake' }
  ];

  useEffect(() => {
    setAnimatedServices(servicesData.map(service => ({ ...service, animated: false })));
  }, []);

  const handleServiceClick = (service) => {
    console.log('Service clicked:', service);
    console.log('Service ID:', service.id);
    console.log('All services:', servicesData);
    
    const foundService = servicesData.find(s => s.id === service.id);
    console.log('Found service:', foundService);
    
    setSelectedService(service);
    setIsModalOpen(true);
    setAnimatedServices(prev => 
      prev.map(s => 
        s.id === service.id ? { ...s, animated: true } : s
      )
    );
  };

  const handleLearnMoreClick = (service, e) => {
    e.stopPropagation();
    handleServiceClick(service);
  };

  const closeModal = () => {
    console.log('Closing modal');
    setIsModalOpen(false);
    setSelectedService(null);
  };

  const handleImageLoad = (serviceId) => {
    setAnimatedServices(prev =>
      prev.map(service =>
        service.id === serviceId ? { ...service, imageLoaded: true } : service
      )
    );
  };

  const scrollToServices = () => {
    const servicesSection = document.querySelector('.services-grid-section');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Inline styles for background images
  const heroStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.85) 0%, rgba(229, 62, 62, 0.7) 50%, rgba(221, 107, 32, 0.5) 100%), url('/images/con2%20copy.jpg')`
  };

  const ctaStyle = {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.85) 0%, rgba(26, 54, 93, 0.9) 100%), url('/images/con2%20copy.jpg')`
  };

  return (
    <div className="services-page">
      {/* Modern Hero Section with Background Image */}
      <section className="modern-hero" style={heroStyle}>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="badge">
                <span>Professional Services</span>
              </div>
              <h1>
                <span className="highlight-text">Expert</span> Consulting,
                <br />
                <span className="highlight-text">Proven</span> Results
              </h1>
              <p className="hero-description">
                Comprehensive consulting solutions backed by extensive experience and measurable outcomes. 
                We deliver excellence across research, monitoring & evaluation, capacity building, and organizational development.
              </p>
              
              <div className="hero-actions">
                <button className="btn-primary" onClick={scrollToServices}>
                  <span>Explore Our Services</span>
                  <i className="fas fa-arrow-down"></i>
                </button>
                <a href="/contact" className="btn-secondary">
                  <i className="fas fa-paper-plane"></i>
                  Get In Touch
                </a>
              </div>
            </div>

            <div className="hero-visual">
              <div className="stats-grid">
                {serviceStats.map((stat, index) => (
                  <div key={index} className="stat-card" style={{ animationDelay: `${index * 0.1}s` }}>
                    <div className="stat-icon">
                      <i className={stat.icon}></i>
                    </div>
                    <div className="stat-content">
                      <div className="stat-number">{stat.number}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="scroll-indicator">
          <div className="scroll-line"></div>
          <span>Scroll to explore</span>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="services-grid-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Professional Services</h2>
            <p>Comprehensive consulting solutions tailored to drive your organizational success and impact</p>
          </div>

          <div className="services-grid">
            {animatedServices.map((service, index) => (
              <div 
                key={service.id} 
                className={`service-card ${service.imageLoaded ? 'image-loaded' : ''}`}
                style={{ animationDelay: `${index * 0.1}s` }}
                onClick={() => handleServiceClick(service)}
              >
                <div className="service-image-container">
                  <div 
                    className="service-image"
                    style={{ 
                      backgroundImage: `url(${service.image})`,
                      '--service-color': service.color
                    }}
                  >
                    <img 
                      src={service.image} 
                      alt={service.title}
                      onLoad={() => handleImageLoad(service.id)}
                      style={{ display: 'none' }}
                    />
                    <div className="image-overlay"></div>
                    <div className="service-badge" style={{ backgroundColor: service.color }}>
                      <i className={service.icon}></i>
                    </div>
                  </div>
                </div>
                
                <div className="service-content">
                  <div className="service-header">
                    <h3>{service.title}</h3>
                    <div className="service-abbreviation" style={{ color: service.color }}>
                      {service.abbreviation}
                    </div>
                  </div>
                  
                  <p className="service-description">{service.description}</p>
                  
                  <div className="service-features">
                    {service.features.slice(0, 3).map((feature, featureIndex) => (
                      <div key={featureIndex} className="feature-item">
                        <i className="fas fa-check" style={{ color: service.color }}></i>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="service-cta">
                    <button 
                      className="learn-more-btn" 
                      style={{ '--hover-color': service.color }}
                      onClick={(e) => handleLearnMoreClick(service, e)}
                    >
                      View Projects & Details
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Process */}
      <ServiceProcess />
{/* Final CTA with Background Image */}
<section className="final-cta" style={ctaStyle}>
  <div className="floating-elements">
    <div className="floating-element"></div>
    <div className="floating-element"></div>
    <div className="floating-element"></div>
    <div className="floating-element"></div>
  </div>
  <div className="container">
    <div className="cta-content">
      <h2>Ready to Start Your Project?</h2>
      <p>Partner with us to drive meaningful change and achieve sustainable results with our expert consulting services.</p>
      <div className="cta-buttons">
        <a href="/contact" className="btn btn-primary">
          <i className="fas fa-rocket"></i>
          Start Your Project
        </a>
        <a href="/portfolio" className="btn btn-secondary">
          <i className="fas fa-briefcase"></i>
          View Our Portfolio
        </a>
      </div>
    </div>
  </div>
</section>

      {/* Service Modal */}
      <ServiceModal 
        service={selectedService} 
        isOpen={isModalOpen} 
        onClose={closeModal} 
      />
    </div>
  );
};

export default ServicesPage;